import { MdPeople } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Badge from "components/badge/Badge";
import Button from "components/button";
import AlertPane from "components/alert";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { Checkbox, SearchInput } from "components/fields";
import { getApiErrorMessage } from "api";
import { formatNumber } from "utils/format";
import ChoiceCard from "components/heartbeat/ChoiceCard";
import OptionGroup from "components/heartbeat/OptionGroup";
import TriggerConfiguration from "components/heartbeat/TriggerConfiguration";
import RecipientFilterBuilder from "components/heartbeat/RecipientFilterBuilder";

const RecipientList = ({ editor }) => {
  const {
    filteredRecipients,
    selectedRecipients,
    toggleRecipient,
    recipientSearch,
    loadingRecipients,
    recipientsError,
    fetchPotentialRecipients,
  } = editor;

  if (loadingRecipients) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (recipientsError) {
    return (
      <ErrorState
        title="Couldn't Load Recipients"
        message={getApiErrorMessage(recipientsError, "Could not load potential recipients")}
        onRetry={fetchPotentialRecipients}
      />
    );
  }

  if (filteredRecipients.length === 0) {
    return (
      <EmptyState
        variant="filtered"
        icon={MdPeople}
        title={recipientSearch ? "No recipients match your search" : "No potential recipients found"}
      />
    );
  }

  return (
    <ul
      aria-label="Potential recipients"
      className="max-h-96 overflow-y-auto rounded-control border border-line"
    >
      {filteredRecipients.map((recipient) => {
        const isSelected = selectedRecipients.includes(recipient.id);
        const hasSurvey = !!recipient.guestSurveyCompletedAt;
        const surveyPending = recipient.guestSurveySentAt && !recipient.guestSurveyCompletedAt;
        const isNew = !recipient.guestSurveySentAt;

        return (
          <li
            key={recipient.id}
            className={`flex items-center justify-between gap-3 border-b border-line px-4 py-3 last:border-b-0 ${
              isSelected ? "bg-brand-50 dark:bg-brand-900/20" : ""
            }`}
          >
            <Checkbox
              label={`${recipient.firstName} ${recipient.lastName}`}
              description={recipient.email || recipient.phone || "No contact info"}
              checked={isSelected}
              onChange={() => toggleRecipient(recipient.id)}
            />
            <div className="flex shrink-0 items-center gap-2">
              {isNew && <Badge tone="info">New</Badge>}
              {hasSurvey && <Badge tone="success">Completed</Badge>}
              {surveyPending && <Badge tone="warning">Pending</Badge>}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const RecipientsSection = ({ editor }) => {
  const {
    isEdit,
    formData,
    handleChange,
    potentialRecipients,
    selectedRecipients,
    selectAllRecipients,
    deselectAllRecipients,
    recipientSearch,
    setRecipientSearch,
    setFilterRecipientCount,
  } = editor;

  return (
    <Card padding="md" className="gap-6">
      <CardHeader title="Survey Distribution" />
      <OptionGroup label="Recipient Mode">
        <div className="grid gap-4 md:grid-cols-3">
          <ChoiceCard
            selected={formData.recipientMode === "automatic"}
            onClick={() => handleChange("recipientMode", "automatic")}
            title="Automatic"
            description="Surveys are sent based on trigger events"
          />
          <ChoiceCard
            selected={formData.recipientMode === "custom"}
            onClick={() => handleChange("recipientMode", "custom")}
            title="Custom"
            description="Select specific members to receive the survey"
          />
          <ChoiceCard
            selected={formData.recipientMode === "manual"}
            onClick={() => handleChange("recipientMode", "manual")}
            title="Manual (QR/Link)"
            description="Share via QR code or link for open access"
          >
            <Badge tone="success" className="mt-2">
              Guest-friendly
            </Badge>
          </ChoiceCard>
        </div>
      </OptionGroup>

      {formData.recipientMode === "automatic" && (
        <div className="space-y-6">
          <TriggerConfiguration
            triggerType={formData.triggerType}
            triggerConfig={formData.triggerConfig}
            onTriggerTypeChange={(type) => handleChange("triggerType", type)}
            onTriggerConfigChange={(config) => handleChange("triggerConfig", config)}
          />

          <section className="space-y-3">
            <div>
              <h3 className="font-semibold text-fg">Recipient Filters (Optional)</h3>
              <p className="mt-1 text-sm text-fg-muted">
                Filter which members receive surveys when the trigger fires. Leave empty to
                include all matching members.
              </p>
            </div>
            <RecipientFilterBuilder
              initialFilters={formData.filterCriteria || {}}
              onFilterChange={(criteria) => handleChange("filterCriteria", criteria)}
              onRecipientCountChange={setFilterRecipientCount}
              showPreview={true}
            />
          </section>
        </div>
      )}

      {formData.recipientMode === "manual" && (
        <AlertPane tone="info" className="" title="QR Code & Direct Link Access">
          <p>
            {isEdit
              ? "A QR code and shareable link have been generated for this campaign. View them in the Delivery Settings tab."
              : "A QR code and shareable link will be automatically generated when you save this campaign."}
          </p>
          <p className="mt-2">
            This mode is ideal for guest cards, lobby displays, or event handouts where anyone can
            scan to take the survey.
          </p>
        </AlertPane>
      )}

      {formData.recipientMode === "custom" && (
        <div className="space-y-6">
          <section className="space-y-3">
            <div>
              <h3 className="font-semibold text-fg">Filter Recipients</h3>
              <p className="mt-1 text-sm text-fg-muted">
                Use filters to narrow down who receives this survey. The count shows matching
                recipients.
              </p>
            </div>
            <RecipientFilterBuilder
              initialFilters={formData.filterCriteria || {}}
              onFilterChange={(criteria) => handleChange("filterCriteria", criteria)}
              onRecipientCountChange={setFilterRecipientCount}
              showPreview={true}
            />
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="font-semibold text-fg">Additional Manual Selection (Optional)</h3>
                <p className="mt-1 text-sm text-fg-muted">
                  Optionally select specific members in addition to filters
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Badge tone="brand">{formatNumber(selectedRecipients.length)} selected</Badge>
                <span className="text-fg-muted">
                  of {formatNumber(potentialRecipients.length)} available
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <SearchInput
                className="flex-1"
                ariaLabel="Search recipients"
                placeholder="Search by name or email..."
                value={recipientSearch}
                onChange={setRecipientSearch}
              />
              <div className="flex gap-2">
                <Button variant="secondary" onClick={selectAllRecipients}>
                  Select All
                </Button>
                <Button variant="secondary" onClick={deselectAllRecipients}>
                  Deselect All
                </Button>
              </div>
            </div>

            <RecipientList editor={editor} />

            <p className="text-sm text-fg-muted">
              Selected recipients will receive the survey when you send the campaign. Members who
              have already completed a survey are marked as "Completed".
            </p>
          </section>
        </div>
      )}
    </Card>
  );
};

export default RecipientsSection;
